/**
 * API_CONFIG.BASE_URL resolution order:
 *   1. VITE_API_BASE_URL env var (set in Vercel / .env.local)
 *   2. Hard-coded production fallback (Render)
 *
 * This project uses Vite. Env vars are prefixed VITE_* and injected at build
 * time via vite.config.ts define — accessible as process.env['VITE_X'] in both
 * browser (esbuild inlines them) and Jest (reads native process.env).
 *
 * NOTE: api.dystest.com returns a proxy-level 503 without CORS headers for
 * browser Origin requests from www.dystest.com. Always route to the Render
 * backend directly until the custom domain proxy is fully repaired.
 */
const RENDER_API_BASE_URL = "https://dystest-backend.onrender.com/api/v1";

const normalizeApiBaseUrl = (url: string | undefined): string => {
  const configured = url?.trim().replace(/\/$/, "");
  if (!configured || configured.startsWith("https://api.dystest.com")) {
    return RENDER_API_BASE_URL;
  }
  return configured;
};

export const API_CONFIG = {
  BASE_URL: normalizeApiBaseUrl(process.env['VITE_API_BASE_URL']),
  FALLBACK_BASE_URL: RENDER_API_BASE_URL,
  LANGUAGES: "/languages/",
  RETRIES: 3,
  RETRY_DELAY: 900,
  TIMEOUT: 15000,
} as const;

export const ANIMATION_DELAYS = {
  FAST: 0.1,
  MEDIUM: 0.3,
  SLOW: 0.6,
  SECTION: 0.8,
} as const;

export const FALLBACK_LANGUAGES = [
  { id: 1, language_code: "en", language_name: "English",    flag_image_url: "https://flagcdn.com/w320/gb.png" },
  { id: 2, language_code: "de", language_name: "German",     flag_image_url: "https://flagcdn.com/w320/de.png" },
  { id: 3, language_code: "fr", language_name: "French",     flag_image_url: "https://flagcdn.com/w320/fr.png" },
  { id: 4, language_code: "nl", language_name: "Dutch",      flag_image_url: "https://flagcdn.com/w320/nl.png" },
  { id: 5, language_code: "hi", language_name: "Hindi",      flag_image_url: "https://flagcdn.com/w320/in.png" },
  { id: 6, language_code: "es", language_name: "Spanish",    flag_image_url: "https://flagcdn.com/w320/es.png" },
  { id: 7, language_code: "pt", language_name: "Portuguese", flag_image_url: "https://flagcdn.com/w320/pt.png" },
  { id: 8, language_code: "it", language_name: "Italian",    flag_image_url: "https://flagcdn.com/w320/it.png" },
] as const;

export const TECHNOLOGY_FEATURES = [
  {
    icon: "👁️",
    title: "Eyeball Tracking",
    text: "We measure gaze stability, fixation duration, and saccadic movement to reveal how visual focus shifts during reading.",
  },
  {
    icon: "⌨️",
    title: "Keyboard Dynamics",
    text: "Keystroke rhythm, hesitation, and correction speed expose sequencing and phonological challenges unique to dyslexic profiles.",
  },
  {
    icon: "😊",
    title: "Facial Emotion Recognition (FER)",
    text: "Subtle expressions reveal frustration, engagement, or relief — helping our AI sense the emotional layer behind reading effort.",
  },
] as const;
